/**
 * Build-version guard — detects (and self-heals) a stale tab after a deploy.
 *
 * The service worker serves hashed JS/CSS from its precache but always fetches
 * `index.html` fresh (NavigationRoute + nginx no-cache). After a deploy an open
 * tab can keep running the *previous* build's JS/CSS while the server has moved
 * on to a new build — a silent mismatch that renders as broken styling (e.g. the
 * auth brand panel losing its dark gradient) and clears only on a hard refresh,
 * because a normal reload still goes through the service worker.
 *
 * Unlike a chunk-load failure (handled in main.js) or the SW `controllerchange`
 * reload (registerServiceWorker.js), a CSS/asset skew raises no error, so nothing
 * else catches it. This guard compares the running bundle's commit hash (baked in
 * at build time) against the hash stamped into the freshly-fetched `index.html`
 * (<meta name="app-build">). On a genuine mismatch it reloads once per target
 * version (sessionStorage-guarded against loops) so the tab realigns to the
 * current build.
 */

import { reloadWhenIdle } from '@/utils/deferredReload';

const RUNNING_HASH = process.env.VUE_APP_COMMIT_HASH || '';
const GUARD_PREFIX = 'buildVersionReload:';

/** Extract the `app-build` meta content from raw index.html markup. */
export function parseServedHash(html) {
  if (typeof html !== 'string') return '';
  // Match the <meta name="app-build"> tag in either attribute order.
  const tag = html.match(/<meta\b[^>]*\bname=["']app-build["'][^>]*>/i);
  if (!tag) return '';
  const content = tag[0].match(/\bcontent=["']([^"']*)["']/i);
  return content ? content[1].trim() : '';
}

/**
 * Fetch the server's current index.html and reload once if the running build no
 * longer matches it. Dependencies are injected for testability.
 */
export async function checkBuildVersion({
  runningHash = RUNNING_HASH,
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  // Defer: never reload mid-interaction — see deferredReload.js.
  reload = () => reloadWhenIdle(),
} = {}) {
  // Skip when we can't reason about versions: dev/local builds have no real hash.
  if (!runningHash || runningHash === 'local') return false;
  if (!fetchImpl || !storage) return false;

  let servedHash = '';
  try {
    const res = await fetchImpl('/index.html', { cache: 'no-store' });
    if (!res || !res.ok) return false;
    servedHash = parseServedHash(await res.text());
  } catch {
    // Offline / network error — nothing actionable.
    return false;
  }

  if (!servedHash || servedHash === runningHash) return false;

  // Reload at most once per target version so a server that keeps handing back a
  // version this tab can't load (should not happen) can never loop.
  const guardKey = GUARD_PREFIX + servedHash;
  if (storage.getItem(guardKey)) return false;
  storage.setItem(guardKey, '1');
  reload();
  return true;
}

/**
 * Start watching for stale builds: once at boot and again whenever the tab
 * becomes visible (catches deploys that land while the tab is backgrounded).
 */
export function startBuildVersionGuard() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  checkBuildVersion();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkBuildVersion();
  });
}
